import { Injectable } from '@angular/core';
import * as JSZip from 'jszip';
import * as FileSaver from 'file-saver';
import { ActiveService } from '@features/active/services';

export interface CarteiraDiariaSegregadoParams {
  dateFrom: string;
  dateTo: string;
  formatType: string;
  language: string;
  fundList: { fundCode: string }[];
  reportLabel: string;
}

export interface MovimentoTransitorioSegregadoParams {
  dateFrom: string;
  dateTo: string;
  formatType: string;
  language: string;
  fundList: { fundCode: string }[];
  reportLabel: string;
}

export interface CashFlowSegregadoParams {
  dateFrom: string;
  dateTo: string;
  formatType: string;
  language: string;
  fundList: { fundCode: string }[];
  reportLabel: string;
  reportType: string;
}

@Injectable({
  providedIn: 'root',
})
export class ReportSegregadoService {
  constructor(private readonly activeService: ActiveService) {}

  /**
   * Generates Daily Portfolio report for each fund, collects results into a ZIP and triggers one download.
   * Used only when consolidarOption is 'segregated' and mode is sync (no async, ≤40 funds).
   */
  async generateCarteiraDiariaSegregatedZip(params: CarteiraDiariaSegregadoParams): Promise<void> {
    const { dateFrom, dateTo, formatType, language, fundList, reportLabel } = params;
    const formatUpper = formatType.toUpperCase();
    const extension = this.getExtension(formatUpper);
    const mimeType = this.getMimeType(formatUpper);
    const zip = new JSZip();
    const errors: string[] = [];

    for (const fund of fundList) {
      const fundCode = fund.fundCode;
      const body = {
        dateFrom,
        dateTo,
        formatType: formatUpper === 'EXCEL' ? 'XLS' : formatUpper === 'PDF' ? 'PDF' : formatUpper,
        status: fundCode,
        language,
        reportId: '',
        reportRequestId: '',
      };

      try {
        const res = await this.activeService.generateCarteiraDiariaReport(body).toPromise();
        if (res?.report) {
          const blob = this.base64ToBlob(res.report, mimeType);
          const fileName = `${reportLabel}_${fundCode}.${extension}`;
          zip.file(fileName, blob);
        }
      } catch (err) {
        errors.push(fundCode);
      }
    }

    const fileCount = Object.keys(zip.files).length;
    if (fileCount === 0) {
      throw new Error('SEGREGADO_NO_FILES');
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const zipName = `CarteiraDiaria_segregado_${dateFrom}.zip`;
    FileSaver.saveAs(zipBlob, zipName);

    if (errors.length > 0) {
      throw new Error(`SEGREGADO_PARTIAL:${errors.join(',')}`);
    }
  }

  /**
   * Generates Temporary Movement report for each fund, collects results into a ZIP and triggers one download.
   * Used only when consolidarOption is 'segregated' and mode is sync (no async, ≤40 funds).
   */
  async generateMovimentoTransitorioSegregatedZip(params: MovimentoTransitorioSegregadoParams): Promise<void> {
    const { dateFrom, dateTo, formatType, language, fundList, reportLabel } = params;
    const formatUpper = formatType.toUpperCase();
    const extension = this.getExtension(formatUpper);
    const mimeType = this.getMimeType(formatUpper);
    const zip = new JSZip();
    const errors: string[] = [];

    for (const fund of fundList) {
      const fundCode = fund.fundCode;
      const body = {
        dateFrom,
        dateTo,
        formatType: formatUpper === 'EXCEL' ? 'XLS' : formatUpper === 'PDF' ? 'PDF' : formatUpper,
        funds: JSON.stringify([fundCode]),
        language,
        reportId: '',
        reportRequestId: '',
      };

      try {
        const res = await this.activeService.generateMovimentoTransitorio(body).toPromise();
        if (res?.report) {
          const blob = this.base64ToBlob(res.report, mimeType);
          const fileName = `${reportLabel}_${fundCode}.${extension}`;
          zip.file(fileName, blob);
        }
      } catch (err) {
        errors.push(fundCode);
      }
    }

    const fileCount = Object.keys(zip.files).length;
    if (fileCount === 0) {
      throw new Error('SEGREGADO_NO_FILES');
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const zipName = `MovimentoTransitorio_segregado_${dateFrom}.zip`;
    FileSaver.saveAs(zipBlob, zipName);

    if (errors.length > 0) {
      throw new Error(`SEGREGADO_PARTIAL:${errors.join(',')}`);
    }
  }

  /**
   * Generates Cash flow (Fluxo de Caixa) report for each fund, collects results into a ZIP and triggers one download.
   * Used only when consolidarOption is 'segregated' and mode is sync (no async, ≤40 funds).
   */
  async generateCashFlowSegregatedZip(params: CashFlowSegregadoParams): Promise<void> {
    const { dateFrom, dateTo, formatType, language, fundList, reportLabel, reportType } = params;
    const formatUpper = formatType.toUpperCase();
    const extension = this.getExtension(formatUpper);
    const mimeType = this.getMimeType(formatUpper);
    const zip = new JSZip();
    const errors: string[] = [];
    const pollIntervalMs = 10_000;
    const maxWaitMs = 20 * 60 * 1000;

    for (const fund of fundList) {
      const fundCode = fund.fundCode;
      const groupoffund = { groupingByFund: [{ fundCode }] };
      const body = {
        dateFrom,
        dateTo,
        formatType: formatUpper,
        language,
        mediaType: 'SFTP',
        groupoffund,
        reportRequestId: '',
        reportType,
        status: '',
        reportId: '',
      };

      try {
        const res = await this.activeService.generateReport(body).toPromise();
        if (res?.errorDescription === 'INRES' && res?.errorCode === '500') {
          errors.push(fundCode);
          continue;
        }
        if (res?.status !== 'running') {
          errors.push(fundCode);
          continue;
        }

        const dowloadBody = {
          dateFrom,
          dateTo,
          formatType: formatUpper,
          language,
          reportId: '',
          reportRequestId: res.reportRequestId,
          reportType,
          status: '',
        };

        const res1 = await this.pollDownloadReport(dowloadBody, pollIntervalMs, maxWaitMs);
        if (res1?.status === 'completed' && res1?.report) {
          const blob = this.base64ToBlob(res1.report, mimeType);
          const fileName = `${reportLabel}_${fundCode}.${extension}`;
          zip.file(fileName, blob);
        } else {
          errors.push(fundCode);
        }
      } catch {
        errors.push(fundCode);
      }
    }

    const fileCount = Object.keys(zip.files).length;
    if (fileCount === 0) {
      throw new Error('SEGREGADO_NO_FILES');
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const zipName = `CashFlow_segregado_${dateFrom}.zip`;
    FileSaver.saveAs(zipBlob, zipName);

    if (errors.length > 0) {
      throw new Error(`SEGREGADO_PARTIAL:${errors.join(',')}`);
    }
  }

  private pollDownloadReport(
    dowloadBody: { reportRequestId: string; [key: string]: unknown },
    intervalMs: number,
    maxWaitMs: number
  ): Promise<{ status: string; report?: string }> {
    const start = Date.now();
    return new Promise((resolve, reject) => {
      const poll = () => {
        this.activeService.downloadReport(dowloadBody).toPromise().then((res1: { status?: string; report?: string } | undefined) => {
          if (res1?.status === 'completed' || res1?.status === 'failed') {
            resolve({ status: res1?.status ?? 'failed', report: res1?.report });
            return;
          }
          if (Date.now() - start >= maxWaitMs) {
            resolve({ status: 'failed' });
            return;
          }
          setTimeout(poll, intervalMs);
        }).catch(reject);
      };
      poll();
    });
  }

  private getExtension(formatType: string): string {
    if (formatType === 'EXCEL') return 'xlsx';
    if (formatType === 'CSV') return 'csv';
    return 'pdf';
  }

  private getMimeType(formatType: string): string {
    if (formatType === 'EXCEL') return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    if (formatType === 'CSV') return 'text/csv';
    return 'application/pdf';
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    const sliceSize = 1024;
    const byteCharacters = atob(base64);
    const bytesLength = byteCharacters.length;
    const slicesCount = Math.ceil(bytesLength / sliceSize);
    const byteArrays: Uint8Array[] = [];

    for (let sliceIndex = 0; sliceIndex < slicesCount; sliceIndex++) {
      const begin = sliceIndex * sliceSize;
      const end = Math.min(begin + sliceSize, bytesLength);
      const bytes = new Uint8Array(end - begin);
      for (let i = 0; i < bytes.length; i++) {
        bytes[i] = byteCharacters.charCodeAt(begin + i);
      }
      byteArrays.push(bytes);
    }
    return new Blob(byteArrays as BlobPart[], { type: mimeType });
  }
}
